import { useState, useEffect } from 'react'
import endpointMapper from '../_endpoints'
import { APPLICATION_BEHAVIOUR } from '../_enums'
import { usePost } from '../_hooks/usePost'
import { useFetch } from '../_hooks/useFetch'
import { useDispatch, useSelector } from 'react-redux'
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr'
import { toast } from 'react-toastify'
import moment from 'moment'
import {
  addNotificationGroup,
  getNotificationGroup,
  getNotificationGroups,
  removeNotificationGroup,
  updateNotificationGroupMembers,
  getNotificationGroupMembers,
  sendNotification,
  getNotifications,
  getNewNotificationCount,
  setNotificationContext,
  addNewNotificationToContext,
  setScheduleNotifications,
  addNewScheduleNotification,
  notificationContextSetNewCount,
  updateNotification
} from '../_redux/actions/notificationActions'
import { getConfig } from '../configs/config'

export const useNotificationService = () => {
  const [, postAddGroup] = usePost()
  const [, postUpdateGroup] = usePost()
  const [, postRemoveGroup] = usePost()
  const [, postMembers] = usePost()
  const [, getGroupById] = useFetch()
  const [groupListIsLoading, getGroupList] = useFetch()
  const [groupMemberListIsLoading, getGroupMemberList] = useFetch()
  const [schedulesLoading, getScheduleNotifications] = useFetch()
  const [, deleteScheduleNotification] = usePost()
  const [, postNotification] = usePost()
  const [, postEmail] = usePost()
  const [, putNotifications] = usePost()
  const [, postContributors] = usePost()
  const [, getNotificationList] = useFetch()
  const [, getAppIcons] = useFetch()
  const [connection, setConnection] = useState()
  const dispatch = useDispatch()
  const userProfile = JSON.parse(localStorage.getItem('userInfoData'))
  const config = getConfig()

  const connectSignalR = () => {
    const connect = new HubConnectionBuilder()
      .withUrl(`${config.REACT_APP_API_URL}/api/bff/notification?target=desktop`, {
        headers: {
          ['x-ms-client-principal-id']: userProfile?.email?.toLowerCase()
        }
      })
      .withAutomaticReconnect()
      .build()

    setConnection(connect)
  }

  const get = async (date) => {
    const endpoint = endpointMapper.notifications.getNotifications
      .replace(':userName', userProfile?.email?.toLowerCase())
      .replace(':currentDate', date)
    const response = await getNotificationList(endpoint)
    dispatch(getNotifications(date))
    return response
  }

  const getNewCount = async (date) => {
    const endpoint = endpointMapper.notifications.getNotificationsCount
      .replace(':userName', userProfile?.email?.toLowerCase())
      .replace(':currentDate', date)
    const response = await getNotificationList(endpoint)
    dispatch(getNewNotificationCount(date))
    return response
  }

  const markAllNotificationsAsSeen = async () => {
    const endpoint = endpointMapper.notifications.putNotifications.replace(
      ':userName',
      userProfile?.email?.toLowerCase()
    )
    await putNotifications({ url: endpoint, method: 'put' })
  }

  const scheduleNotification = async () => {
    const endpoint = endpointMapper.notifications.getNotifications
      .replace(':userName', userProfile?.email?.toLowerCase())
      .replace(':currentDate', moment().toISOString())
    const notifications = await getNotificationList(endpoint)
    dispatch(
      setScheduleNotifications({ notifications, isBannerVisible: notifications?.length > 0 })
    )
    return notifications
  }

  const refreshNotifications = async (daysAgo = 7) => {
    const today = new Date()
    const date = new Date(today.setDate(today.getDate() - daysAgo))
    const notificationList = await get(moment(date).format('YYYY-MM-DD'))
    const notifications = notificationList.sort((item) => item.createdAt).reverse()
    dispatch(setNotificationContext({ notifications }))
    return notifications
  }

  const refreshNewCount = async () => {
    const today = new Date()
    const aMonthAgo = new Date(today.setDate(today.getDate() - 30))
    const count = await getNewCount(moment(aMonthAgo).format('YYYY-MM-DD'))
    dispatch(notificationContextSetNewCount(count))
  }

  const getIcons = async (appIds) => {
    return getAppIcons(`${endpointMapper.applications.getIcons}?appIds=${appIds.toString()}`)
  }

  useEffect(() => {
    if (connection) {
      connection
        .start()
        .then(() => {
          connection.on('receiveNotifications', (message) => {
            const messageObject = JSON.parse(message)
            messageObject.notificationType === 0
              ? dispatch(addNewNotificationToContext(messageObject))
              : dispatch(addNewScheduleNotification(messageObject))
          })
        })
        .catch((error) => console.log(error))
    }
  }, [connection])

  const modifyGroup = async (item, isUpdate = false) => {
    const response = await postAddGroup({
      url: endpointMapper.applicationGroups[isUpdate ? 'updateGroup' : 'postOnFly'],
      data: {
        ...item,
        deleted: false,
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
        createdBy: userProfile?.id,
        modifiedBy: userProfile?.id
      }
    })

    // dispatch(addNotificationGroup(item))
    return response
  }

  const getSchedules = async () => {
    const endpoint = endpointMapper.notifications.getScheduledNotifications
    const response = await getScheduleNotifications(endpoint)
    return [schedulesLoading, response]
  }

  const deleteSchedule = async (id) => {
    const response = await deleteScheduleNotification({
      url: endpointMapper.notifications.deleteSchedule,
      data: {
        notificatoinIds: [id]
      }
    })

    return response
  }

  const removeGroup = async (item) => {
    const response = await postRemoveGroup({
      url: endpointMapper.applicationGroups.deleteApplicationGroup.replace(':groupId', item)
    })

    return response
  }

  const updateMembers = async (item) => {
    const response = await postMembers({
      url: endpointMapper.bff.updateNotificationGroupMembers,
      data: item
    })

    dispatch(updateNotificationGroupMembers(item))
    return response
  }

  const getGroup = async (id) => {
    const endpoint = endpointMapper.applicationGroups.get.replace(':appGroupId', id)
    const response = await getGroupById(endpoint)
    dispatch(getNotificationGroup(id))
    return response
  }

  const getGroups = async () => {
    const response = await getGroupList(endpointMapper.applicationGroups.getList)
    dispatch(getNotificationGroups())
    return [groupListIsLoading, response]
  }

  const getGroupMembers = async (id) => {
    const endpoint = endpointMapper.bff.getNotificationGroupMembers
    const response = await getGroupMemberList(
      `${endpoint}&ApplicationGroupId=${id}&ReturnAllUsers=true`
    )
    dispatch(getNotificationGroupMembers(id))
    return [groupMemberListIsLoading, response]
  }

  const send = async (item) => {
    const response = await postNotification({
      url: endpointMapper.notifications.sendNotification,
      data: {
        ...item,
        sender: userProfile?.email,
        sendEmail: false,
        sendToSignalR: true
      }
    })

    dispatch(sendNotification(item))
    return response
  }

  const sendEmail = async (item) => {
    const response = await postEmail({
      url: endpointMapper.notifications.sendEmail,
      data: {
        ...item
      }
    })
    return response
  }

  const update = async (item) => {
    const response = await putNotifications({
      url: endpointMapper.notifications.updateNotification,
      data: {
        ...item,
        sender: userProfile?.email,
        isActive: true
      },
      method: 'put'
    })

    dispatch(updateNotification(item))
    return response
  }

  const addContributors = async (params) => {
    const response = await postContributors({
      url: endpointMapper.applications.saveContributors,
      data: {
        uniqueName: params.id,
        usersToAdd: params.usersToAdd,
        usersToRemove: params.usersToRemove
      }
    })

    return response
  }

  return {
    modifyGroup,
    removeGroup,
    getGroup,
    getGroups,
    getGroupMembers,
    updateMembers,
    addContributors,
    send,
    sendEmail,
    update,
    scheduleNotification,
    refreshNotifications,
    refreshNewCount,
    getIcons,
    getSchedules,
    deleteSchedule,
    getNewCount,
    markAllNotificationsAsSeen,
    connectSignalR
  }
}
